import { Injectable } from '@angular/core';
import { LocalStorageService } from '../../shared/storage/local-storage.service';

export interface IAccessToken {
  token: string;
  refreshToken: string;
}

@Injectable()
export class SessionTokenService {

  static ACCESS_TOKEN_STORAGE_KEY = '__gpt__';
  static REFRESH_TOKEN_STORAGE_KEY = '__gprt__';
  static ACCESS_TOKEN_STORAGE_ADMIN_KEY = '__gpta__';
  static REFRESH_TOKEN_STORAGE_ADMIN_KEY = '__gprta__';
  static ACCESS_TOKEN_EXPIRES_AT_KEY = '__gp_eat__';
  static REFRESH_TOKEN_EXPIRES_AT_KEY = '__gp_reat__';
  static REFRESH_TOKEN_EXPIRES_HOURS = 20;
  static SSO_SESSION_KEY = '__gp_sso__';

  constructor(private localStorageService: LocalStorageService) {
  }

  persist(accessToken: IAccessToken): void {
    this.localStorageService.save(SessionTokenService.ACCESS_TOKEN_STORAGE_KEY, accessToken.token);
    this.localStorageService.save(SessionTokenService.REFRESH_TOKEN_STORAGE_KEY, accessToken.refreshToken);
  }

  persistWithExpiry(accessToken: IAccessToken, expiresIn: number): void {
    this.persist(accessToken);
    const now = Date.now();
    this.localStorageService.save(SessionTokenService.ACCESS_TOKEN_EXPIRES_AT_KEY, (now + expiresIn * 1000).toString());
    this.localStorageService.save(SessionTokenService.REFRESH_TOKEN_EXPIRES_AT_KEY, (now + SessionTokenService.REFRESH_TOKEN_EXPIRES_HOURS * 60 * 60 * 1000).toString());
  }

  isAccessTokenExpired(): boolean {
    const expiresAt = parseInt(this.localStorageService.get(SessionTokenService.ACCESS_TOKEN_EXPIRES_AT_KEY) || '0', 10);
    if (!expiresAt) {
      return true;
    }
    return Date.now() >= expiresAt;
  }

  isRefreshTokenExpired(): boolean {
    const expiresAt = parseInt(this.localStorageService.get(SessionTokenService.REFRESH_TOKEN_EXPIRES_AT_KEY) || '0', 10);
    if (!expiresAt) {
      return false;
    }
    return Date.now() >= expiresAt;
  }

  persistSSOSession(): void {
    this.localStorageService.save(SessionTokenService.SSO_SESSION_KEY, 'true');
  }

  isSSOSession(): boolean {
    return this.localStorageService.has(SessionTokenService.SSO_SESSION_KEY);
  }

  destroy(): void {
    this.localStorageService.clear(SessionTokenService.ACCESS_TOKEN_STORAGE_KEY);
    this.localStorageService.clear(SessionTokenService.REFRESH_TOKEN_STORAGE_KEY);
    this.localStorageService.clear(SessionTokenService.ACCESS_TOKEN_EXPIRES_AT_KEY);
    this.localStorageService.clear(SessionTokenService.REFRESH_TOKEN_EXPIRES_AT_KEY);
    this.localStorageService.clear(SessionTokenService.SSO_SESSION_KEY);
  }

  isAuthenticated(): boolean {
    return this.localStorageService.has(SessionTokenService.ACCESS_TOKEN_STORAGE_KEY)
      && this.localStorageService.has(SessionTokenService.REFRESH_TOKEN_STORAGE_KEY);
  }

  getToken(): IAccessToken {
    const accessToken: IAccessToken = {
      token: this.localStorageService.get(SessionTokenService.ACCESS_TOKEN_STORAGE_KEY),
      refreshToken: this.localStorageService.get(SessionTokenService.REFRESH_TOKEN_STORAGE_KEY)
    };
    return accessToken;
  }

  storeAdminToken(accessToken: IAccessToken): void {
    this.localStorageService.save(SessionTokenService.ACCESS_TOKEN_STORAGE_ADMIN_KEY, accessToken.token);
    this.localStorageService.save(SessionTokenService.REFRESH_TOKEN_STORAGE_ADMIN_KEY, accessToken.refreshToken);
  }

  destroyAdminToken(): void {
    this.localStorageService.clear(SessionTokenService.ACCESS_TOKEN_STORAGE_ADMIN_KEY);
    this.localStorageService.clear(SessionTokenService.REFRESH_TOKEN_STORAGE_ADMIN_KEY);
  }

  getAdminToken(): IAccessToken {
    const accessToken: IAccessToken = {
      token: this.localStorageService.get(SessionTokenService.ACCESS_TOKEN_STORAGE_ADMIN_KEY),
      refreshToken: this.localStorageService.get(SessionTokenService.REFRESH_TOKEN_STORAGE_ADMIN_KEY)
    };
    return accessToken;
  }
}
